import { AlertSize } from '../../features/alert/alert';
import { MessageType } from '../../features/alert/global-message.interface';
import { PRODUCT_PK12_ONLY_ERROR_CODE } from './cart-util';
import { ProductCardData } from '../../features/product-card/product-card-display';
import { html } from 'lit';
import { isProfessional } from './product-util';

/**
 * Renders an alert on the product card component
 *
 * @param id the id for the alert
 * @param product a product object coming from the product card
 * @param alertSize the size of the alert
 */
export function cardAlert(id: string, product: ProductCardData, alertSize: AlertSize) {
  if (!product.errorMessage) {
    return html``;
  }

  return html`
    <mhecomm-alert
      id="${id}"
      type=${getMessageType(product)}
      size=${alertSize}
      message=${product.errorMessage}
      data-automation-id="product-error-message-${product?.isbn}"
    >
    </mhecomm-alert>
  `;
}

/**
 * Gets the message type for the alert
 *
 * @param product a product object coming from the product card component
 */
export function getMessageType(product: ProductCardData) {
  if (!product?.isPublished && product?.errorCode !== PRODUCT_PK12_ONLY_ERROR_CODE) {
    return MessageType.primary;
  }

  return MessageType.warning;
}

/**
 * Renders the item price for products
 *
 * If the product quantity is greater than one, it will render the item price
 *
 * @param product a product object coming from the product card component
 * @param productMhidIsbn the product mhid or isbn
 */
export function renderItemPrice(product: ProductCardData, productMhidIsbn: string | undefined) {
  if (product.quantity! > 1) {
    return html`
      <div
        class="d-flex justify-content-end text-end fs-small">
        <span class="fw-semibold" data-automation-id="item-price-${productMhidIsbn}">Item Price:&nbsp;</span>
          $${product.netPrice?.toFixed(2)}
      </div>`
  }

  return html``;
}

/**
 * Renders the total price for products
 *
 * @param product a product object coming from the product card component
 * @param productMhidIsbn the product mhid or isbn
 */
export function renderPrice(product: ProductCardData, productMhidIsbn: string | undefined) {
  let verbiage = product.isDiscountedBundle ? "Bundle Offer:" : "Price:";
  
  return html`
    <div class="d-flex justify-content-end text-end">
      <p class="mb-0">
        <span>${verbiage}</span>
        <span
          class="fw-bold mb-0"
          data-automation-id="total-item-price-${productMhidIsbn}"
        >
          ${product.totalPrice !== undefined
            ? html`$${product.totalPrice.toFixed(2)} ${product?.isFreeTrial ? 'Today' : ''}`
            : ''}
        </span>
        <span
          id="price-live-region-${product.isbn13 || product.isbn || product.id}"
          class="visually-hidden"
          aria-live="polite"
          aria-atomic="true"
        ></span>
      </p>
    </div>
  `;
}

/**
 * Renders the original price
 *
 * @param product a product object coming from the product card component
 */
export function renderOriginalPrice(product: ProductCardData, productMhidIsbn: string | undefined) {
  return html`
      <div
        class="d-flex justify-content-end text-end">
        <span data-automation-id="original-price-${productMhidIsbn}">Original Price:&nbsp;</span>
        <span class="fw-bold mb-0 text-decoration-line-through">$${(parseFloat(product.listPrice!.toFixed(2)) * product.quantity!).toFixed(2)}</span>
      </div>`;
}

/**
 * Renders the quantity if the product is not professional
 *
 * @param product a product object coming from the product card component
 */
export function renderQuantity(product: ProductCardData) {
  if (isProfessional(product.businessUnit)) {
    return html``;
  }

  return html`<span>Quantity: ${product.quantity}</span>`;
}
